import { Fragment, RefObject, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AllocationOfCostsChartResponseByCategory } from '../../../../api/financials-client/financials-client.type';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { truncateLabel } from '../../../../helpers/string-helpers/string-helpers';
import { LineChartDot } from '../../../strategy/initiative-performance/components/line-chart/line-chart-dot';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as AllocationOfCostsLegend } from '../shared/Legend';
import { assignColorsToItems, formatXAxisLabel, getItemColor } from '../shared/shared.helpers';
import {
  addTrendLines,
  filterAllocationOfCostsData,
  maybeGetNullLabelForSeries,
  transformAllocationOfCostsData,
} from './allocation-of-costs.helpers';

function Chart({
  data,
  chartRef,
}: {
  data: AllocationOfCostsChartResponseByCategory | undefined;
  chartRef: RefObject<HTMLDivElement>;
}) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const {
    viewBy,
    filters: {
      allocationOfCosts: { selectedMeasure, selectedOptions, selectedTrendLines },
    },
  } = useFinancialsStore();

  const filteredData = filterAllocationOfCostsData(data, selectedMeasure, selectedOptions, null);
  const chartData = transformAllocationOfCostsData(filteredData, viewBy);

  const chartDataWithTrendLines = addTrendLines(chartData);
  const colorAssignments = assignColorsToItems(selectedOptions, DEFAULT_CHART_COLORS);

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer>
        <LineChart data={chartDataWithTrendLines}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={formatXAxisLabel} tick={{ fontSize: 12 }} />
          <YAxis yAxisId="cost" orientation="left" tick={{ fontSize: 12 }} />
          <YAxis yAxisId="discrete" orientation="right" tick={{ fontSize: 12 }} />
          <Tooltip
            content={
              <CustomTooltip
                filterFn={(item) => selectedOptions.includes(item.name || '')}
                nameFormatter={(item) => truncateLabel(maybeGetNullLabelForSeries(item.name!))}
                viewBy={viewBy}
              />
            }
          />
          {selectedOptions.map((option) => {
            const color = getItemColor(option, colorAssignments);
            return (
              <Fragment key={option}>
                <Line
                  yAxisId="cost"
                  type="monotone"
                  dataKey={option}
                  stroke={color}
                  strokeWidth={hoveredItem === null || hoveredItem === option ? 2 : 1}
                  opacity={hoveredItem === null || hoveredItem === option ? 1 : 0.3}
                  fill={color}
                  key={option}
                  dot={hoveredItem === option ? <LineChartDot color={color} /> : undefined}
                />
                <Line
                  yAxisId="cost"
                  type="monotone"
                  dataKey={`${option}_trend`}
                  stroke={color}
                  strokeWidth={hoveredItem === null || hoveredItem === option ? 2 : 1}
                  strokeDasharray="5 5"
                  opacity={hoveredItem === null || hoveredItem === option ? 1 : 0.3}
                  fill={color}
                  key={`${option}_trend`}
                  hide={!(selectedTrendLines || []).includes(option)}
                />
              </Fragment>
            );
          })}
          <Legend content={<AllocationOfCostsLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Chart };
